import React from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Phone, MessageCircle, MapPin, Mail, ArrowRight, Award, Users, Target, Heart } from 'lucide-react';
import { companyInfo, stats } from '../mock';

const About = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/91${companyInfo.whatsapp}?text=Hi, I want to know more about UNITYPRO HOMES`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:+91${companyInfo.phones[0]}`;
  };

  const values = [
    {
      icon: Award,
      title: "Excellence",
      description: "Committed to delivering the highest quality properties and services to our clients"
    },
    {
      icon: Users,
      title: "Trust",
      description: "Building lasting relationships through transparency and honest dealings"
    },
    {
      icon: Target,
      title: "Innovation",
      description: "Constantly evolving to bring you the best investment opportunities"
    },
    {
      icon: Heart,
      title: "Customer First",
      description: "Your satisfaction and dreams are at the heart of everything we do"
    }
  ];

  return (
    <div className="about-page min-h-screen bg-black pt-24 pb-20">
      {/* Hero Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <div className="flex justify-center mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_2a73fc45-adbf-44a6-8139-8de8d79a04e7/artifacts/09g17uyg_ChatGPT%20Image%20Jan%202%2C%202026%2C%2005_19_57%20PM.png" 
                alt="UNITYPRO HOMES Logo"
                className="h-28 md:h-36 lg:h-40 w-auto object-contain"
              />
            </div>
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              About Us
            </Badge>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6">
              <span className="gradient-text">{companyInfo.name}</span>
            </h1>
            <p className="text-xl md:text-2xl text-orange-400 font-semibold mb-3 md:mb-4">{companyInfo.slogan}</p>
            <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto px-4">
              {companyInfo.tagline}
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card text-center">
                <div className="text-5xl font-bold gradient-text mb-2">{stat.value}</div>
                <div className="text-gray-400">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Managing Director Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <Card className="md-card overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="md-image-wrapper relative">
                <img 
                  src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&h=800&fit=crop" 
                  alt="Managing Director"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
              </div>
              
              <div className="p-12 flex flex-col justify-center bg-gradient-to-br from-gray-900 to-black">
                <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50 w-fit">
                  Leadership
                </Badge>
                <h2 className="text-4xl font-bold mb-2 text-white">{companyInfo.managingDirector}</h2>
                <p className="text-xl text-orange-400 mb-6">{companyInfo.designation}</p>
                
                <div className="space-y-4 text-gray-300">
                  <p className="text-lg leading-relaxed">
                    With over a decade of experience in the real estate industry, {companyInfo.managingDirector} has been 
                    instrumental in transforming UNITYPRO HOMES into one of Delhi's most trusted property developers.
                  </p>
                  <p className="text-lg leading-relaxed">
                    His vision of creating "खुशियों का नया address" drives the company's commitment to helping 
                    families find their dream homes and make smart property investments.
                  </p>
                  <p className="text-lg leading-relaxed">
                    Under his leadership, UNITYPRO HOMES has successfully delivered 50+ projects and served 
                    over 1000+ happy customers across Delhi and NCR.
                  </p>
                </div>

                <div className="mt-8 flex gap-4">
                  <Button className="cta-button-primary" onClick={handleWhatsApp}>
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Connect on WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
                Our Journey
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="gradient-text">Building Dreams Since 2010</span>
              </h2>
            </div>

            <Card className="story-card p-10">
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  UNITYPRO HOMES was founded with a simple yet powerful vision - to make quality real estate 
                  accessible to every family in Delhi. What started as a small property dealing firm has grown 
                  into a trusted name in residential and commercial real estate.
                </p>
                <p>
                  Located strategically in Kapashera, Delhi, we specialize in premium residential plots, modern homes, 
                  farmhouses, and commercial properties across Delhi and NCR. Our focus areas include sectors along 
                  Delhi-Alwar Road, IMT Highway, KMP Expressway, and Sohna Road.
                </p>
                <p>
                  We believe in transparent dealings, clear documentation, and customer satisfaction above all. 
                  Every property we offer is thoroughly verified, legally compliant, and represents genuine value 
                  for your investment.
                </p>
                <p>
                  Our tagline "खुशियों का नया address" reflects our commitment - we don't just sell properties, 
                  we help create addresses where happiness lives.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/50">
              Our Values
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="gradient-text">What Drives Us</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="value-card p-8 text-center">
                  <div className="value-icon-wrapper mb-6">
                    <IconComponent className="w-12 h-12" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-white">{value.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{value.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <Card className="cta-card p-12 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              Let's Start Your Property Journey Together
            </h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
              Connect with us today and discover how we can help you find the perfect property
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center mb-12">
              <Button size="lg" className="cta-button-primary group" onClick={handleWhatsApp}>
                <MessageCircle className="mr-2 w-5 h-5" />
                WhatsApp Us
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" className="cta-button-secondary" onClick={handleCall}>
                <Phone className="mr-2 w-5 h-5" />
                Call Now
              </Button>
            </div>

            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="contact-info-card">
                <Phone className="w-6 h-6 text-orange-400 mb-3" />
                <div className="text-sm text-gray-500 mb-1">Call Us</div>
                {companyInfo.phones.map((phone, idx) => (
                  <div key={idx} className="text-white font-semibold">+91 {phone}</div>
                ))}
              </div>
              
              <div className="contact-info-card">
                <MessageCircle className="w-6 h-6 text-orange-400 mb-3" />
                <div className="text-sm text-gray-500 mb-1">WhatsApp</div>
                <div className="text-white font-semibold">+91 {companyInfo.whatsapp}</div>
              </div>
              
              <div className="contact-info-card">
                <MapPin className="w-6 h-6 text-orange-400 mb-3" />
                <div className="text-sm text-gray-500 mb-1">Visit Us</div>
                <div className="text-white font-semibold">{companyInfo.address}</div>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default About;